/**
 * SDK atoms — the ONE installed `LocalAgentSDK` and its projections.
 *
 * The SDK is constructed ONCE per JS realm by `installSDKAction` from a
 * resolved bootstrap, and never rebuilt (sdk ADR-008):
 * a rebuild would re-mint the web device identity, orphan the chat bridge
 * source and platform-call executor, and duplicate the daemon `/events`
 * socket's ref count (`sdk ADR-006`). Address and identity answers are asked
 * of the SDK — `sdk.baseUrl`, `sdk.hasDaemon`, `await sdk.deviceId()` — never
 * held here.
 *
 * The SDK instance is rebuilt never. Token churn does not touch it: the
 * AuthProvider reads the token live from the store.
 */

import { atom } from 'jotai';
import {
  BootRefused,
  SingleFlight,
  bootAfterResolve,
  bootstrapRealm,
  createSDK,
  hasDaemonAddress,
  publishesClient,
  settleDaemonBoot,
  WebStorageAdapter,
} from '@localagent/sdk';
import type { SdkBootstrap, LocalAgentSDK as SDK } from '@localagent/sdk';
import { getPlatformRef } from '@localagent/platform';
import { configureAtomDevtools } from '../utils/tracking';
import { resetAccountScopedStores } from '../auth/teardown';
import {
  bindIdentity,
} from '../auth/authState';
import { getRegisteredStytchClient } from '../../auth/StytchSessionAuthProvider';
import { hydrateStytchSession } from '../../auth/hydrateStytchSession';
import { DEFAULT_SESSION_DURATION_MINUTES } from '../../auth/sessionDuration';
import { store } from '../index';
import {
  addNotificationEntryAction,
  unreadableCredentialNotification,
} from '../notifications/atoms';
import { showNotificationToast } from '../../components/notifications/notification-toasts';

/** True once the SDK is installed; `getSDK()` will not throw. SDK-dependent
 *  React-Query hooks MUST gate `enabled` on this. */
export const sdkConfiguredAtom = configureAtomDevtools(
  atom((get) => get(sdkAtom) !== null),
  'sdk/configured',
);

/** The installed SDK's backend origin — a projection of SDK state, never a
 *  source of it. Null until the SDK is installed. */
export const apiBaseUrlAtom = configureAtomDevtools(
  atom<string | null>((get) => get(sdkAtom)?.baseUrl ?? null),
  'sdk/apiBaseUrl',
);

/** The single per-tab cloud {@link BridgeEventStream} (web only), published here by
 * `useWebBridgeClient` so app-wide consumers (session events, platform_call,
 * notification/error demux, indexing, cloud filesChanged) register handlers on
 * the SAME connection — never a second socket (one connection per tab). Null on
 * desktop, where the daemon owns the cloud bridge (bridge ADR-001) and the
 * renderer consumes {@link daemonPushFrameStreamAtom} instead, and before login. */
export const bridgeEventStreamAtom = configureAtomDevtools(
  atom<import('@localagent/sdk').BridgeEventStream | null>(null),
  'sdk/bridgeEventStream',
);

/** The desktop notification/error push stream over the daemon's loopback
 * `/events` relay, published by `useWebBridgeClient` (events ADR-012). Null on
 * web and before login. */
export const daemonPushFrameStreamAtom = configureAtomDevtools(
  atom<import('@localagent/sdk').ShogunChopPushFrameStream | null>(null),
  'sdk/daemonPushFrameStream',
);

/**
 * The installed SDK. Writable ONLY as a test/story override: setting a non-null
 * instance installs it (production boot goes through {@link installSDKAction});
 * setting null clears the override.
 */
const sdkOverrideAtom = configureAtomDevtools(
  atom<SDK | null>(null),
  'sdk/instance',
);

export const sdkAtom = configureAtomDevtools(
  atom<SDK | null, [SDK | null], void>(
    (get) => {
      const override = get(sdkOverrideAtom);
      if (override !== null) {
        return override;
      }
      return get(installedSdkAtom);
    },
    (_get, set, override: SDK | null) => set(sdkOverrideAtom, override),
  ),
  'sdk/atom',
);

/** The SDK the boot installed. Written exactly once. */
const installedSdkAtom = configureAtomDevtools(atom<SDK | null>(null), 'sdk/installed');

export const BOOTSTRAP_SOURCE = 'sdk/bootstrap';

/** The installed client, visible to the credential callbacks that need it
 *  (the renewal round trip rides the SAME client). */
const sdkRef: { current: SDK | null } = { current: null };

/** Two overlapping installs join one construction. */
const installFlight = new SingleFlight<SDK>();

/** Constructed once (sdk ADR-008), published once its authority answers (sdk ADR-013). */
export async function installSDKAction(): Promise<SDK> {
  const installed = store.get(installedSdkAtom);
  if (installed !== null) {
    return installed;
  }
  return installFlight.run(constructAndInstallSDK);
}

async function constructAndInstallSDK(): Promise<SDK> {
  const platform = getPlatformRef();
  const channel = platform.bootstrapChannel?.bind(platform);
  const bootstrap: SdkBootstrap & { deviceId?: string } = channel
    ? await channel()
    : await (async () => {
        // The web arm: the SDK's own browser resolver (VITE_BACKEND_URL > page
        // origin, daemon null).
        const { resolveBrowserBootstrap } = await import('@localagent/sdk');
        return resolveBrowserBootstrap();
      })();

  const realm = bootstrapRealm(bootstrap);
  const resolved = bootAfterResolve(bootstrap);
  if (resolved.state === 'refused') {
    throw new BootRefused(
      'daemon-missing',
      'The ShogunChop background service is not running. Start it, then retry.',
    );
  }

  const sdk = createSDK({
    bootstrap: {
      backendUrl: bootstrap.backendUrl,
      daemonUrl: bootstrap.daemonUrl,
      realm,
      historyPageSize: bootstrap.historyPageSize,
      historyWindowCeiling: bootstrap.historyWindowCeiling,
    },
    deviceId: bootstrap.deviceId,
    credential: {
      // The correlating dimension on this feature's log events (sdk ADR-027).
      realm,
      // The ONE durable adapter for this realm: the browser profile's
      // localStorage, over the SDK's enumerable seam (sdk ADR-011).
      storage: new WebStorageAdapter(window.localStorage),
      // The browser arm's renewal SOURCE — the host-registered Stytch client,
      // read + hydrated here so the whole round trip is one injected callback
      // (sdk ADR-010).
      renewSource: async () => {
        const stytch = getRegisteredStytchClient();
        const tokens = stytch?.session.getTokens();
        if (!stytch || !tokens?.session_token) return null;
        const client = sdkRef.current;
        if (client === null) {
          throw new Error('renewSource: the SDK client vanished mid-renew');
        }
        const renewed = await client.auth.renewSession({
          session_token: tokens.session_token,
          session_duration_minutes: DEFAULT_SESSION_DURATION_MINUTES,
        });
        await hydrateStytchSession(stytch, {
          session_jwt: renewed.session_jwt,
          session_token: renewed.session_token,
        });
        return {
          session_jwt: renewed.session_jwt,
          session_token: renewed.session_token,
        };
      },
      // Host rotation hook: a rotation OBSERVED here is hydrated into the
      // Stytch client so its cookie never diverges from the phase.
      adopt: async (material) => {
        const stytch = getRegisteredStytchClient();
        if (stytch) {
          await hydrateStytchSession(stytch, {
            session_jwt: material.session_jwt,
            session_token: material.session_token,
          });
        }
      },
    },
  });

  // The shell's projections bind to the controller; the teardown barriers are
  // registered ONCE so every route to no_credential AND every in-place
  // credential swap reclaims the shell's account-scoped stores before the new
  // phase commits (the SDK reclaims its own through its gate).
  bindIdentity(
    sdk.identity,
    store as unknown as { set: (a: unknown, v: unknown) => void },
  );
  sdk.identity.registerTeardown(async () => {
    await resetAccountScopedStores(store.get, store.set);
  });
  sdk.identity.registerIdentityChangeTeardown(async () => {
    await resetAccountScopedStores(store.get, store.set, 'identity-change');
  });

  sdkRef.current = sdk;

  // The daemon's phase is asked for and applied before publication (sdk ADR-013).
  if (resolved.state === 'snapshotting') {
    if (!hasDaemonAddress(bootstrap)) {
      throw new Error('installSDK: a snapshotting boot names a daemon address');
    }
    const boot = await settleDaemonBoot(sdk, { realm, daemonUrl: bootstrap.daemonUrl });
    if (!publishesClient(boot)) {
      sdkRef.current = null;
      sdk.dispose();
      throw new BootRefused(
        'snapshot-unavailable',
        "Can't reach the ShogunChop background service. It did not report its sign-in state, " +
          'so the app cannot tell whether you are signed in. Retry once the service is up.',
      );
    }
  }

  store.set(installedSdkAtom, sdk);
  raiseUnreadableCredentialNotice(sdk);
  return sdk;
}

/** A discarded saved sign-in is announced where the user will see it,
 *  beside the SDK's structured `identity.credential_unreadable` log. */
function raiseUnreadableCredentialNotice(sdk: SDK): void {
  const diagnostic = sdk.identity.restoreDiagnostic;
  if (diagnostic === null) return;
  const entry = unreadableCredentialNotification(diagnostic.cause);
  store.set(addNotificationEntryAction, entry);
  showNotificationToast({ kind: entry.kind, title: entry.title, body: entry.body });
}
